import { useCallback } from 'react';

import {
  useGetGraphqlDataQuery,
  useLazyGetGraphqlDataQuery,
} from '../graphqlService';
import {
  type BaseQueryOptions,
  processQueryData,
  type QueryResult,
  useIndexedDBBase,
} from './useIndexedDBBase';

export interface GraphQLQueryOptions<T, R = never> extends BaseQueryOptions<T, R> {
  document: string;
  variables?: Record<string, any>;
}

export interface QueryCallOptions {
  document: string;
}

export type LazyQueryTrigger = (options?: QueryCallOptions, forceRefetch?: boolean) => void;

export type LazyIndexedDBQueryResult<T, R> = [LazyQueryTrigger, QueryResult<T, R>];

// Non-lazy version
export function useIndexedDBGraphqlQuery<T, R = never>({
  cacheKey,
  document,
  variables,
  transform,
  skip = false,
  revalidate = false,
  cacheLifetime,
}: GraphQLQueryOptions<T, R>): QueryResult<T, R> {
  const {
    localData,
    isLoadingCache,
    shouldFetch,
  } = useIndexedDBBase<T, R>({ cacheKey, skip, cacheLifetime });

  const skipQuery = skip || (!shouldFetch && !revalidate && localData !== null);

  const { data: apiResponse, isLoading, error } = useGetGraphqlDataQuery({
    document,
    variables,
    cache: true,
    cacheKey,
    cacheLifetime,
  }, {
    skip: skipQuery,
  });

  const transformedData = processQueryData(apiResponse as T, localData, transform);

  return {
    data: transformedData,
    isLoading: isLoadingCache || isLoading,
    error,
  };
}

// Lazy version
export function useLazyIndexedDBGraphqlQuery<T, R = never>({
  cacheKey,
  document,
  variables,
  transform,
  skip = false,
  cacheLifetime,
}: GraphQLQueryOptions<T, R>): LazyIndexedDBQueryResult<T, R> {
  const {
    localData,
    isLoadingCache,
  } = useIndexedDBBase<T, R>({ cacheKey, skip, cacheLifetime });

  const [fetchTrigger, queryResult] = useLazyGetGraphqlDataQuery();

  const trigger: LazyQueryTrigger = useCallback((options?: QueryCallOptions) => {
    fetchTrigger({
      document: options?.document || document,
      variables,
      cache: true,
      cacheKey,
      cacheLifetime,
    });
  }, [fetchTrigger, document, variables, cacheKey, cacheLifetime]);

  const transformedData = processQueryData(queryResult.data as T, localData, transform);

  return [
    trigger,
    {
      data: transformedData,
      isLoading: isLoadingCache || queryResult.isLoading,
      error: queryResult.error,
    },
  ];
}
